// src/services/lawyerService.js

import axiosInstance from "../config/axiosInstance";

const BASE_URL = "/lawyers";

/**
 * Obtiene una lista paginada de abogados con opción de búsqueda.
 * @param {number} page - Número de página (inicia en 1)
 * @param {number} size - Tamaño de página (elementos por página)
 * @param {string|null} searchTerm - Término de búsqueda (opcional)
 * @returns {Promise<ApiResponse<PaginatedResult<LawyerListItemDto>>>}
 */
export const getLawyersPaged = async (
  page = 1,
  size = 10,
  searchTerm = null
) => {
  const response = await axiosInstance.get(
    `${BASE_URL}/paged?pageIndex=${page}&pageSize=${size}&searchTerm=${
      searchTerm ?? ""
    }`
  );
  return response.data;
};

/**
 * Obtiene un abogado por su ID.
 * @param {string} id - ID del abogado
 * @returns {Promise<ApiResponse<LawyerDetailDto>>}
 */
export const getLawyerById = async (id) => {
  const response = await axiosInstance.get(`${BASE_URL}/${id}`);
  return response.data;
};

/**
 * Crea un nuevo abogado (incluye creación del usuario y asignación de roles).
 * @param {LawyerCreateDto} data - Datos para crear el abogado
 * @returns {Promise<ApiResponse<Guid>>}
 */
export const createLawyer = async (data) => {
  const response = await axiosInstance.post(BASE_URL, data);
  return response.data;
};

/**
 * Actualiza un abogado existente.
 * @param {Guid} id - ID del abogado
 * @param {LawyerUpdateDto} data - Datos actualizados del abogado
 * @returns {Promise<ApiResponse<boolean>>}
 */
export const updateLawyer = async (id, data) => {
  const response = await axiosInstance.put(`${BASE_URL}/${id}`, data);
  return response.data;
};

/**
 * Elimina (soft delete) un abogado por su ID.
 * @param {string} id - ID del abogado
 * @returns {Promise<ApiResponse<boolean>>}
 */
export const deleteLawyer = async (id) => {
  const response = await axiosInstance.delete(`${BASE_URL}/${id}`);
  return response.data;
};

/**
 * Carga o reemplaza la foto de perfil del abogado.
 * @param {string} id - ID del abogado
 * @param {File} file - Archivo de imagen (IFormFile)
 * @returns {Promise<ApiResponse<Guid>>}
 */
export const uploadLawyerPhoto = async (id, file) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await axiosInstance.post(
    `${BASE_URL}/${id}/photo`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );

  return response.data;
};

/**
 * Carga adjuntos para un abogado específico.
 * @param {string} id - ID del abogado.
 * @param {File[]} files - Archivos a cargar.
 * @param {string} attachmentTypeId - Tipo de adjunto (CategoryItemId).
 * @param {string} [notes] - Notas opcionales asociadas al adjunto.
 * @returns {Promise<ApiResponse<ReadonlyArray<Guid>>>}
 */
export const uploadLawyerAttachments = async (
  id,
  files,
  attachmentTypeId,
  notes = ""
) => {
  const formData = new FormData();

  files.forEach((file) => formData.append("files", file));
  formData.append("attachmentTypeId", attachmentTypeId);

  //nuevo campo agregado
  if (notes !== undefined && notes !== null) {
    formData.append("notes", notes);
  }

  const response = await axiosInstance.post(
    `${BASE_URL}/${id}/attachments`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );

  return response.data;
};

/**
 * Elimina un adjunto de un abogado.
 * @param {string} attachmentId - ID del adjunto
 * @returns {Promise<ApiResponse<boolean>>}
 */
export const deleteLawyerAttachment = async (attachmentId) => {
  try {
    const response = await axiosInstance.delete(
      `${BASE_URL}/attachments/${attachmentId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error al eliminar el adjunto del abogado:", error);
    throw error;
  }
};

/**
 * Obtiene el DTO de reporte completo de un abogado.
 * @param {string} id - ID del abogado
 * @returns {Promise<ApiResponse<LawyerReportDto>>}
 */
export const getLawyerReport = async (id) => {
  try {
    const response = await axiosInstance.get(`${BASE_URL}/${id}/report`);
    return response.data; // ApiResponse<LawyerReportDto>
  } catch (error) {
    console.error("Error al obtener el reporte del abogado:", error);
    throw error;
  }
};

/**
 * Obtiene una lista paginada de abogados eliminados (lógico).
 * @param {number} page - Número de página (1..N)
 * @param {number} size - Tamaño de página
 * @param {string|null} searchTerm - Término de búsqueda (opcional)
 * @returns {Promise<ApiResponse<PaginatedResult<LawyerDeletedListItemDto>>>}
 */
export const getDeletedLawyersPaged = async (
  page = 1,
  size = 10,
  searchTerm = null
) => {
  const { data } = await axiosInstance.get(
    `${BASE_URL}/deleted/paged?pageIndex=${page}&pageSize=${size}&searchTerm=${
      searchTerm ?? ""
    }`
  );
  return data;
};

/**
 * Restaura (undo soft-delete) un abogado por Id.
 * Requiere permiso de restauración en backend.
 * @param {string} id - Guid del abogado
 * @returns {Promise<ApiResponse<boolean>>}
 */
export const restoreLawyer = async (id) => {
  const { data } = await axiosInstance.post(`${BASE_URL}/${id}/restore`);
  return data;
};

/**
 * Obtiene el listado de abogados activos para usar en selects (combos).
 * Backend: GET /lawyers/active-lookup
 * Devuelve una lista de LawyerLookupItemDto:
 *   { id, fullName, documentNumber, email }
 *
 * @returns {Promise<ApiResponse<LawyerLookupItemDto[]>>}
 */
export const getActiveLawyersLookup = async () => {
  const { data } = await axiosInstance.get(`${BASE_URL}/active-lookup`);
  return data;
};
